import type {
  MessageQueueAnalyticsFilters,
  MqBreakdownDimension,
  MqBreakdownRow,
} from '../../services/message-queue.service';

export interface DashboardFilters {
  /** `period` as emitted by the time-range filter; undefined means "all". */
  period?: string;
  since?: string;
  until?: string;
  channel: string;
  status: string;
  country: string;
  provider: string;
  merchant: string;
  application: string;
  compare: boolean;
}

export const DEFAULT_FILTERS: DashboardFilters = {
  period: 'today',
  channel: '',
  status: '',
  country: '',
  provider: '',
  merchant: '',
  application: '',
  compare: false,
};

/** Map UI filters to the analytics API query (custom ranges need both ends). */
export function toApiFilters(filters: DashboardFilters): MessageQueueAnalyticsFilters {
  const custom = !filters.period && (filters.since || filters.until);
  const base: MessageQueueAnalyticsFilters = {
    period: custom ? 'custom' : (filters.period ?? 'all'),
    channel: filters.channel || undefined,
    status: filters.status || undefined,
    country: filters.country || undefined,
    processor_provider: filters.provider || undefined,
    merchant: filters.merchant || undefined,
    application: filters.application || undefined,
    compare: filters.compare,
  };
  if (custom) {
    if (!filters.since || !filters.until) {
      return { ...base, period: 'week' };
    }
    base.start_date = new Date(filters.since).toISOString();
    base.end_date = new Date(filters.until).toISOString();
  }
  return base;
}

export type SortKey = 'name' | 'total' | 'sent' | 'failed' | 'success_rate';

export function sortRows(rows: MqBreakdownRow[], key: SortKey, dir: 'asc' | 'desc'): MqBreakdownRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const left = a[key];
    const right = b[key];
    if (left === right) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return (left < right ? -1 : 1) * factor;
  });
}

/** Query params for the dynamic list that match a breakdown row click-through. */
export function listQueryFor(dimension: MqBreakdownDimension, row: MqBreakdownRow): Record<string, string> {
  if (dimension === 'channel') return { channel: row.key };
  if (dimension === 'country' && row.id) return { country: row.id };
  if (dimension === 'provider' && row.id) return { processor_provider: row.id };
  return {};
}

export function formatSeconds(value: number | null): string {
  if (value === null) return '—';
  return value < 60 ? `${value.toFixed(1)} s` : `${(value / 60).toFixed(1)} min`;
}

export function formatPercent(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}
